import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Gem, Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Willkommen zurück.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]";

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-[#0A0A0A] p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <Gem className="absolute -right-10 -top-12 h-72 w-72 text-white/[0.04]" strokeWidth={0.6} />
        <div className="absolute bottom-24 left-1/3 h-56 w-56 rounded-full bg-[#A6A4A9]/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-md border border-white/20 bg-[#0A0A0A]">
            <img src="/stig-rock-gem.png" alt="STIG ROCK" className="h-8 w-auto object-contain" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold tracking-[0.18em]">STIG <span className="font-medium text-[#A6A4A9]">ROCK</span></p>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#A6A4A9]">Brand Protection</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-bold leading-tight">Markenschutz,<br />unter Kontrolle.</h1>
          <p className="mt-4 max-w-sm text-sm text-white/60">
            Unautorisierte Nutzung Ihrer Marke erkennen, dokumentieren und nachverfolgen – an einem Ort.
          </p>
        </div>
        <p className="relative text-xs text-white/40">© STIG ROCK · Compliance Monitor</p>
      </div>

      <div className="flex items-center justify-center bg-[#FAF7F2] p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#E8E4DF]">
              <Gem className="h-5 w-5 text-[#A6A4A9]" strokeWidth={1.4} />
            </div>
            <p className="font-heading text-base font-bold tracking-[0.18em] text-[#0A0A0A]">STIG <span className="font-medium text-[#A6A4A9]">ROCK</span></p>
          </div>

          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Anmelden</h2>
          <p className="mt-1 text-sm text-[#6B6A6E]">Bitte melden Sie sich mit Ihrem Konto an.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className={labelCls}>E-Mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A6A4A9]" />
                <Input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="name@firma.de"
                  className="rounded-md border-[#E0DCD6] bg-white pl-10 focus-visible:ring-[#0A0A0A]"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Passwort</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A6A4A9]" />
                <Input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="rounded-md border-[#E0DCD6] bg-white pl-10 focus-visible:ring-[#0A0A0A]"
                />
              </div>
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-[#0A0A0A] py-6 text-base font-semibold tracking-wide text-white hover:bg-[#262626] disabled:opacity-70"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Anmelden"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
